import React, { useState, useEffect } from 'react';
import { 
  Brain, 
  Target, 
  UserPlus, 
  Settings2, 
  Rocket, 
  ChevronRight, 
  Menu, 
  X,
  Zap,
  TrendingUp,
  Award,
  Languages
} from 'lucide-react';
import ReactCountryFlag from 'react-country-flag';

const CONFIG = {
  // --- PRODUCT DETAILS ---
  PRODUCT_NAME: "IQSkillBoost",
  PRODUCT_SLOGAN: "Boostez votre QI avec des jeux cérébraux",
  PRODUCT_DESCRIPTION: "Améliorez vos capacités cognitives, votre mémoire et vos compétences en résolution de problèmes avec des jeux d'entraînement cérébral conçus scientifiquement.",
  TARGET_AUDIENCE: "Étudiants, professionnels et toute personne souhaitant améliorer ses performances mentales et capacités cognitives.",
  BRAND_VIBE: "Intelligent, engageant, scientifique, axé sur les résultats.",

  // --- FUNCTIONAL SETTINGS ---
  BILINGUAL_SUPPORT: false, // Set to `false` to disable language toggle.
  PRIMARY_LANGUAGE: { name: 'French', code: 'fr', flag: 'FR' },

  // --- CONTENT & FEATURES ---
  CALL_TO_ACTION: "Commencer l'entraînement",
  // Define the 3 main features of your product. 
  // Find icon names at: https://lucide.dev/icons/
  KEY_FEATURES: [
    {
      icon: "Brain", // Icon name from lucide-react
      title: "Jeux conçus scientifiquement",
      description: "Des jeux cérébraux développés par des neuroscientifiques pour cibler des fonctions cognitives spécifiques et améliorer les performances mentales."
    },
    {
      icon: "Target",
      title: "Plans d'entraînement personnalisés",
      description: "Un entraînement adaptatif alimenté par l'IA qui ajuste la difficulté en fonction de vos performances et se concentre sur vos points faibles."
    },
    {
      icon: "TrendingUp",
      title: "Suivez vos progrès",
      description: "Surveillez votre amélioration cognitive avec des analyses détaillées et des métriques de performance au fil du temps."
    }
  ],
  
  // Define the questions and answers for the FAQ section.
  FAQ_DATA: [
    {
      question: "À quelle vitesse verrai-je des améliorations dans mes capacités cognitives ?",
      answer: "La plupart des utilisateurs rapportent des améliorations notables de la mémoire, de la concentration et de la résolution de problèmes en 2-3 semaines d'entraînement cohérent. Les résultats varient selon l'engagement individuel et les niveaux cognitifs de base."
    },
    {
      question: "Ces jeux conviennent-ils à tous les groupes d'âge ?",
      answer: "Oui ! Nos jeux cérébraux sont conçus pour les 8-80 ans et plus. Nous proposons des niveaux de difficulté adaptés à l'âge et du contenu qui évolue avec vous, parfait pour les étudiants, les professionnels et les seniors."
    },
    {
      question: "Combien de temps devrais-je passer à m'entraîner chaque jour ?",
      answer: "Nous recommandons 15-20 minutes d'entraînement quotidien pour des résultats optimaux. Nos jeux sont conçus pour être engageants et peuvent facilement s'intégrer dans votre routine quotidienne, que ce soit pendant les pauses ou les trajets."
    }
  ],
  
  // Define navigation links. The `id` should match the section's HTML id attribute.
  NAV_LINKS: [
    { title: "Fonctionnalités", id: "features" },
    { title: "Comment ça marche", id: "how-it-works" },
    { title: "FAQ", id: "faq" },
  ]
};

// Multi-language content object
const content = {
  en: {
    nav: {
      features: "Features",
      howItWorks: "How It Works",
      faq: "FAQ"
    },
    hero: {
      slogan: "Boost Your IQ with Brain Games",
      description: "Improve your cognitive abilities, memory, and problem-solving skills with scientifically designed brain training games.",
      cta: "Start Training"
    },
    features: {
      title: "Discover the Power of IQSkillBoost",
      features: [
        {
          title: "Scientifically Designed Games",
          description: "Brain games developed by neuroscientists to target specific cognitive functions and improve mental performance."
        },
        {
          title: "Personalized Training Plans",
          description: "AI-powered adaptive training that adjusts difficulty based on your performance and focuses on your weaknesses."
        },
        {
          title: "Track Your Progress",
          description: "Monitor your cognitive improvement with detailed analytics and performance metrics over time."
        }
      ]
    },
    gamesSection: {
      title: "50+ Brain Training Games",
      description: "Our games cover all important cognitive areas: memory, attention, problem-solving, logic, and spatial thinking. Each game has been developed by neuroscientists to maximize cognitive improvements.",
      items: [
        "Memory games for better memorization",
        "Logic puzzles for critical thinking",
        "Attention games for concentration"
      ],
      dailyTraining: "Daily training"
    },
    aiSection: {
      title: "AI-Powered Personalization",
      description: "Our advanced algorithm analyzes your performance and automatically adjusts game difficulty. Training is personalized to strengthen your weakest cognitive areas and maximize improvements.",
      stats: {
        monitoring: "AI Monitoring",
        personalization: "Personalization"
      },
      cta: "Try Now"
    },
    howItWorks: {
      title: "Get Started in 3 Simple Steps",
      steps: [
        { title: "Create Your Account" },
        { title: "Choose Your Games" },
        { title: "Boost Your IQ" }
      ]
    },
    progressSection: {
      title: "Track Your Progress",
      subtitle: "Detailed analytics and metrics show how your cognitive abilities improve over time.",
      visualTitle: "Visualize Your Cognitive Evolution",
      visualDescription: "Track your performance with detailed charts, trends, and analytics that show your progression over time.",
      items: [
        "Real-time performance charts",
        "Comparison with your goals",
        "Weekly and monthly reports"
      ],
      cards: [
        { title: "Performance Trends", description: "See your improvements over weeks and months" },
        { title: "Achievements & Rewards", description: "Earn badges for important milestones" },
        { title: "Instant Feedback", description: "Receive immediate assessments of your performance" }
      ]
    },
    faq: {
      title: "Frequently Asked Questions",
      questions: [
        {
          question: "How quickly will I see improvements in my cognitive abilities?",
          answer: "Most users report noticeable improvements in memory, concentration, and problem-solving after 2-3 weeks of consistent training. Results vary based on individual engagement and baseline cognitive levels."
        },
        {
          question: "Are these games suitable for all age groups?",
          answer: "Yes! Our brain games are designed for ages 8-80 and beyond. We offer age-appropriate difficulty levels and content that evolves with you, perfect for students, professionals, and seniors."
        },
        {
          question: "How much time should I spend training each day?",
          answer: "We recommend 15-20 minutes of daily training for optimal results. Our games are designed to be engaging and can easily fit into your daily routine, whether during breaks or commutes."
        }
      ]
    },
    cta: {
      title: "Ready to Boost Your IQ?",
      button: "Start Training"
    },
    footer: {
      privacy: "Privacy",
      terms: "Terms",
      serviceTitle: "GStore & Squeedio Service",
      serviceDescription: "GStore & Squeedio is a games subscription service. By placing an order with us, you warrant that you are over the age of 18, and either are the bill payer, or have the bill payer's permission.",
      serviceDetails: "You will be charged 1.14 Omani Rial/weekly (5% VAT). Subscription is automatically renewed until you unsubscribe.",
      unsubscribe: "To unsubscribe from GStore, send Unsub to 92261. To unsubscribe from Squeedio, send UNSUB SQD to 92832 (Ooredoo) or UNSUB D to 92292 (Omantel).",
      operatorCharges: "Operator data charges may apply as per your plan.",
      termsConfirmation: "By signing up for and/or using the service you acknowledge and confirm that you have read and understood the Terms & Conditions and Privacy Policy, that you are a resident of Oman, you are 18 years or older, and are the mobile account holder or you have consent from the mobile account holder.",
      rightsNotice: "These Conditions will apply to all services provided by GStore & Squeedio unless stated otherwise. None of these Conditions affect any rights or obligations imposed under the Law.",
      copyright: "© {year} GStore & Squeedio. All rights reserved. Service provided by GoMobee LTD.",
      domainOwner: "This domain is owned by Lead Switch B.V."
    }
  },
  ar: {
    nav: {
      features: "المميزات",
      howItWorks: "كيف يعمل",
      faq: "الأسئلة الشائعة"
    },
    hero: {
      slogan: "عزز ذكاءك بألعاب الدماغ",
      description: "حسّن قدراتك المعرفية وذاكرتك ومهارات حل المشكلات من خلال ألعاب تدريب الدماغ المصممة علميًا.",
      cta: "ابدأ التدريب"
    },
    features: {
      title: "اكتشف قوة IQSkillBoost",
      features: [
        {
          title: "ألعاب مصممة علميًا",
          description: "ألعاب دماغ طورها علماء الأعصاب لاستهداف وظائف معرفية محددة وتحسين الأداء العقلي."
        },
        {
          title: "خطط تدريب مخصصة",
          description: "تدريب تكيفي مدعوم بالذكاء الاصطناعي يعدل الصعوبة بناءً على أدائك ويركز على نقاط ضعفك."
        },
        {
          title: "تابع تقدمك",
          description: "راقب تحسينك المعرفي من خلال التحليلات التفصيلية ومقاييس الأداء بمرور الوقت."
        }
      ]
    },
    gamesSection: {
      title: "أكثر من 50 لعبة تدريب دماغ",
      description: "تشمل ألعابنا جميع المجالات المعرفية المهمة: الذاكرة والانتباه وحل المشكلات والمنطق والتفكير المكاني. كل لعبة طورها علماء الأعصاب لتعظيم التحسينات المعرفية.",
      items: [
        "ألعاب الذاكرة لحفظ أفضل",
        "ألغاز منطقية للتفكير النقدي",
        "ألعاب الانتباه للتركيز"
      ],
      dailyTraining: "التدريب اليومي"
    },
    aiSection: {
      title: "التخصيص المدعوم بالذكاء الاصطناعي",
      description: "يحمّل خوارزميتنا المتقدمة أداءك ويضبط تلقائيًا صعوبة اللعبة. التدريب مخصص لتقوية أضعف مجالاتك المعرفية وتعظيم التحسينات.",
      stats: {
        monitoring: "مراقبة الذكاء الاصطناعي",
        personalization: "التخصيص"
      },
      cta: "جرب الآن"
    },
    howItWorks: {
      title: "ابدأ في 3 خطوات بسيطة",
      steps: [
        { title: "أنشئ حسابك" },
        { title: "اختر ألعابك" },
        { title: "عزز ذكاءك" }
      ]
    },
    progressSection: {
      title: "تابع تقدمك",
      subtitle: "التحليلات التفصيلية والمقاييس تُظهر كيف تتحسن قدراتك المعرفية بمرور الوقت.",
      visualTitle: "تصور تطورك المعرفي",
      visualDescription: "تابع أداءك من خلال الرسوم البيانية التفصيلية والاتجاهات والتحليلات التي تُظهر تقدمك بمرور الوقت.",
      items: [
        "رسوم بيانية للأداء في الوقت الفعلي",
        "المقارنة مع أهدافك",
        "التقارير الأسبوعية والشهرية"
      ],
      cards: [
        { title: "اتجاهات الأداء", description: "شاهد تحسيناتك على مدار الأسابيع والأشهر" },
        { title: "الإنجازات والجوائز", description: "احصل على شارات للمعالم الهامة" },
        { title: "ردود الفعل الفورية", description: "احصل على تقييمات فورية لأدائك" }
      ]
    },
    faq: {
      title: "الأسئلة الشائعة",
      questions: [
        {
          question: "بأي سرعة سأرى تحسينات في قدراتي المعرفية؟",
          answer: "يقارن معظم المستخدمين تحسينات ملحوظة في الذاكرة والتركيز وحل المشكلات بعد 2-3 أسابيع من التدريب المتسق. تختلف النتائج بناءً على المشاركة الفردية ومستويات المعرفة الأساسية."
        },
        {
          question: "هل هذه الألعاب مناسبة لجميع الفئات العمرية؟",
          answer: "نعم! ألعاب الدماغ لدينا مصممة للأعمار من 8-80 عامًا وأكثر. نقدم مستويات صعوبة مناسبة للعمر ومحتوى يتطور معك، مثالي للطلاب والمهنيين وكبار السن."
        },
        {
          question: "كم من الوقت يجب أن أقضي في التدريب كل يوم؟",
          answer: "نوصي بـ15-20 دقيقة من التدريب اليومي للحصول على أفضل النتائج. ألعابنا مصممة لكونها جذابة ويمكنها بسهولة الاندماج في روتينك اليومي، سواء أثناء الاستراحات أو التنقلات."
        }
      ]
    },
    cta: {
      title: "مستعد لتعزيز ذكاءك؟",
      button: "ابدأ التدريب"
    },
    footer: {
      privacy: "الخصوصية",
      terms: "الشروط",
      serviceTitle: "خدمة GStore و Squeedio",
      serviceDescription: "GStore & Squeedio هي خدمة اشتراك في الألعاب. من خلال تقديم طلب معنا، فإنك تلتزم بأنك تجاوزت سن 18 عاماً، وكنت إما دافع الفاتورة، أو لديك إذن من دافع الفاتورة.",
      serviceDetails: "سيتم تحصيل 1.14 ريال عماني/أسبوعي (5% ضريبة القيمة المضافة) منك. يتم تجديد الاشتراك تلقائياً حتى تلغي الاشتراك.",
      unsubscribe: "لإلغاء الاشتراك من GStore، أرسل Unsub إلى 92261. لإلغاء الاشتراك من Squeedio، أرسل UNSUB SQD إلى 92832 (أوريدو) أو UNSUB D إلى 92292 (عمانتل).",
      operatorCharges: "قد تنطبق رسوم بيانات المشغل وفقاً لخطتك.",
      termsConfirmation: "من خلال التسجيل للخدمة و/أو استخدامها فإنك تقر وتؤكد أنك قد قرأت وفهمت الشروط والأحكام وسياسة الخصوصية، وأنك مقيم في عمان، وعمرك 18 عاماً أو أكثر، وأنك صاحب الحساب المحمول أو لديك موافقة من صاحب الحساب المحمول.",
      rightsNotice: "ستطبق هذه الشروط على جميع الخدمات التي تقدمها GStore & Squeedio ما لم يُنص على خلاف ذلك. لا تؤثر أي من هذه الشروط على أي حقوق أو التزامات مفروضة بموجب القانون.",
      copyright: "© {year} GStore & Squeedio. جميع الحقوق محفوظة. الخدمة مقدمة من GoMobee LTD.",
      domainOwner: "هذا المجال مملوك من قبل Lead Switch B.V."
    }
  }
};

function App() {
  const [currentLanguage, setCurrentLanguage] = useState('ar'); // Default to Arabic
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isHeaderScrolled, setIsHeaderScrolled] = useState(false);
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);

  // Handle scroll effect for header
  useEffect(() => {
    const handleScroll = () => {
      setIsHeaderScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Toggle language
  const switchLanguage = (lang) => {
    setCurrentLanguage(lang);
    setIsLanguageMenuOpen(false);
    // Apply RTL for Arabic
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
  };

  // Close mobile menu
  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // Get current content based on language
  const getContent = (key) => {
    return content[currentLanguage]?.[key] || content.en[key];
  };

  // Apply RTL on mount and language change
  useEffect(() => {
    document.documentElement.dir = currentLanguage === 'ar' ? 'rtl' : 'ltr';
  }, [currentLanguage]);

  // Close language menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isLanguageMenuOpen && !event.target.closest('.language-switcher')) {
        setIsLanguageMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isLanguageMenuOpen]);

  return (
    <div className="min-h-screen bg-light font-body">
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isHeaderScrolled ? 'bg-white/95 backdrop-blur-md shadow-2xl shadow-purple-500/20' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            {/* Logo */}
            <div className="flex-shrink-0">
              <div className="h-8 px-3 py-1 bg-gradient-to-r from-primary-600 to-secondary-600 text-white rounded-lg font-bold text-lg flex items-center shadow-lg shadow-purple-500/30">
                <img src="/images/logo.png" alt="IQSkillBoost Logo" className={`w-6 h-6 ${currentLanguage === 'ar' ? 'ml-2' : 'mr-2'}`} />
                {CONFIG.PRODUCT_NAME}
              </div>
            </div>

            {/* Desktop Navigation - Centered */}
            <nav className="hidden md:flex flex-1 justify-center items-center" style={{ gap: '2.5rem' }}>
              <a
                href="#features"
                className="text-dark hover:text-primary-600 transition-all duration-300 font-medium relative group whitespace-nowrap"
              >
                {getContent('nav').features}
                <span className={`absolute -bottom-1 w-0 h-0.5 bg-gradient-to-r from-primary-500 to-secondary-500 transition-all duration-300 group-hover:w-full ${currentLanguage === 'ar' ? 'right-0' : 'left-0'}`}></span>
              </a>
              <a
                href="#how-it-works"
                className="text-dark hover:text-primary-600 transition-all duration-300 font-medium relative group whitespace-nowrap"
              >
                {getContent('nav').howItWorks}
                <span className={`absolute -bottom-1 w-0 h-0.5 bg-gradient-to-r from-primary-500 to-secondary-500 transition-all duration-300 group-hover:w-full ${currentLanguage === 'ar' ? 'right-0' : 'left-0'}`}></span>
              </a>
              <a
                href="#faq"
                className="text-dark hover:text-primary-600 transition-all duration-300 font-medium relative group whitespace-nowrap"
              >
                {getContent('nav').faq}
                <span className={`absolute -bottom-1 w-0 h-0.5 bg-gradient-to-r from-primary-500 to-secondary-500 transition-all duration-300 group-hover:w-full ${currentLanguage === 'ar' ? 'right-0' : 'left-0'}`}></span>
              </a>
            </nav>

            {/* Right side - Language Switcher & CTA */}
            <div className="flex items-center gap-2 md:gap-4 flex-shrink-0">
              {/* Language Switcher */}
              <div className="relative language-switcher">
                <button
                  onClick={() => setIsLanguageMenuOpen(!isLanguageMenuOpen)}
                  className="flex items-center p-2 rounded-lg text-dark hover:bg-purple-100 transition-all duration-200"
                  style={{ gap: '0.5rem' }}
                >
                  <Languages className="w-5 h-5" />
                  {currentLanguage === 'en' ? (
                    <ReactCountryFlag countryCode="US" svg style={{ width: '20px', height: '15px' }} />
                  ) : (
                    <ReactCountryFlag countryCode="OM" svg style={{ width: '20px', height: '15px' }} />
                  )}
                </button>
                
                {/* Language Dropdown */}
                {isLanguageMenuOpen && (
                  <div className="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-xl shadow-purple-500/30 border border-purple-200 overflow-hidden z-50">
                    <button
                      onClick={() => switchLanguage('en')}
                      className={`w-full flex items-center space-x-3 px-4 py-3 hover:bg-purple-50 transition-all ${
                        currentLanguage === 'en' ? 'bg-purple-50' : ''
                      }`}
                    >
                      <ReactCountryFlag countryCode="US" svg style={{ width: '20px', height: '15px' }} />
                      <span className="text-dark font-medium">English</span>
                    </button>
                    <button
                      onClick={() => switchLanguage('ar')}
                      className={`w-full flex items-center space-x-3 px-4 py-3 hover:bg-purple-50 transition-all ${
                        currentLanguage === 'ar' ? 'bg-purple-50' : ''
                      }`}
                    >
                      <ReactCountryFlag countryCode="OM" svg style={{ width: '20px', height: '15px' }} />
                      <span className="text-dark font-medium">العربية</span>
                    </button>
                  </div>
                )}
              </div>

              {/* CTA Button */}
              <a
                href="/s"
                className="bg-gradient-to-r from-primary-600 to-secondary-600 text-white px-4 py-2 md:px-6 rounded-lg hover:from-primary-700 hover:to-secondary-700 transition-all duration-300 font-medium shadow-lg shadow-purple-500/30 hover:shadow-xl hover:shadow-purple-500/40 transform hover:-translate-y-0.5 text-sm md:text-base"
              >
                <span className="hidden md:inline">{getContent('hero').cta}</span>
                <span className="md:hidden">{currentLanguage === 'ar' ? 'ابدأ' : 'Start'}</span>
              </a>

              {/* Mobile menu button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden p-2 rounded-lg text-dark hover:bg-purple-100 transition-all duration-200"
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white/95 backdrop-blur-md border-t border-purple-200">
            <div className="px-4 py-2 space-y-1">
              <a
                href="#features"
                onClick={closeMobileMenu}
                className="block px-3 py-2 text-dark hover:text-primary-600 hover:bg-purple-50 transition-all duration-200 font-medium rounded-lg"
              >
                {getContent('nav').features}
              </a>
              <a
                href="#how-it-works"
                onClick={closeMobileMenu}
                className="block px-3 py-2 text-dark hover:text-primary-600 hover:bg-purple-50 transition-all duration-200 font-medium rounded-lg"
              >
                {getContent('nav').howItWorks}
              </a>
              <a
                href="#faq"
                onClick={closeMobileMenu}
                className="block px-3 py-2 text-dark hover:text-primary-600 hover:bg-purple-50 transition-all duration-200 font-medium rounded-lg"
              >
                {getContent('nav').faq}
              </a>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section id="hero" className="pt-24 pb-16 bg-gradient-to-br from-purple-50 via-primary-50 to-secondary-50 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="w-full h-full bg-purple-mesh animate-pulse"></div>
        </div>
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-32 h-32 bg-primary-300 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-purple-float"></div>
          <div className="absolute top-40 right-10 w-24 h-24 bg-secondary-300 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-purple-float" style={{animationDelay: '1s'}}></div>
          <div className="absolute bottom-20 left-1/4 w-20 h-20 bg-accent-300 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-purple-float" style={{animationDelay: '2s'}}></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="max-w-4xl mx-auto">
            <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-primary-500 to-secondary-500 text-white rounded-full mb-8 shadow-2xl shadow-purple-500/30 animate-purple-glow">
              <Brain className="w-12 h-12" />
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-dark mb-6 font-heading bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
              {getContent('hero').slogan}
            </h1>
            <p className="text-xl md:text-2xl text-gray-700 mb-8 leading-relaxed">
              {getContent('hero').description}
            </p>
            <a
              href="/s"
              className="inline-flex items-center bg-gradient-to-r from-primary-600 to-secondary-600 text-white px-10 py-5 rounded-xl text-xl font-semibold hover:from-primary-700 hover:to-secondary-700 transition-all duration-300 shadow-2xl shadow-purple-500/30 hover:shadow-3xl hover:shadow-purple-500/40 transform hover:-translate-y-1"
            >
              {getContent('hero').cta}
              <ChevronRight className="ml-3 w-6 h-6" />
            </a>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white relative">
        <div className="absolute inset-0 bg-gradient-to-b from-purple-50/50 to-transparent"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-dark mb-16 font-heading">
            <span className="bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
              {getContent('features').title}
            </span>
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {getContent('features').features.map((feature, index) => (
              <div key={index} className="text-center p-8 rounded-2xl bg-gradient-to-br from-purple-50 to-primary-50 hover:from-purple-100 hover:to-primary-100 transition-all duration-500 hover:shadow-2xl hover:shadow-purple-500/20 transform hover:-translate-y-2 border border-purple-100">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-primary-500 to-secondary-500 text-white rounded-2xl mb-6 shadow-lg shadow-purple-500/30">
                  {index === 0 && <Brain className="w-10 h-10" />}
                  {index === 1 && <Target className="w-10 h-10" />}
                  {index === 2 && <TrendingUp className="w-10 h-10" />}
                </div>
                <h3 className="text-xl font-semibold text-dark mb-4 font-heading">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Brain Training Games Section */}
      <section className="py-20 bg-gradient-to-br from-purple-100 via-primary-100 to-secondary-100 relative">
        <div className="absolute inset-0 bg-purple-mesh opacity-5"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-dark mb-6 font-heading">
                <span className="bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
                  {getContent('gamesSection').title}
                </span>
              </h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                {getContent('gamesSection').description}
              </p>
              <ul className="space-y-4">
                {getContent('gamesSection').items.map((item, index) => (
                  <li key={index} className="flex items-start">
                    <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-full flex items-center justify-center mr-3 mt-1">
                      <ChevronRight className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-700">
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <img 
                src="/images/brain-games-hero.png" 
                alt="Brain training games"
                className="rounded-3xl shadow-2xl shadow-purple-500/30 w-full h-80 object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-gradient-to-r from-secondary-500 to-primary-500 text-white p-4 rounded-2xl shadow-xl shadow-purple-500/30">
                <p className="font-semibold text-lg">15 min</p>
                <p className="text-sm">{getContent('gamesSection').dailyTraining}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI-Powered Personalization Section */}
      <section className="py-20 bg-white relative">
        <div className="absolute inset-0 bg-gradient-to-b from-purple-50/50 to-transparent"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <img 
                src="/images/ai-personalization.png" 
                alt="AI-powered personalization"
                className="rounded-2xl shadow-2xl w-full h-80 object-cover"
              />
            </div>
            <div className="order-1 md:order-2">
              <h2 className="text-3xl md:text-4xl font-bold text-dark mb-6 font-heading">
                <span className="bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
                  {getContent('aiSection').title}
                </span>
              </h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                {getContent('aiSection').description}
              </p>
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-gradient-to-br from-purple-50 to-primary-50 p-4 rounded-xl border border-purple-200">
                  <p className="text-2xl font-bold text-primary-600">24/7</p>
                  <p className="text-sm text-gray-600">
                    {getContent('aiSection').stats.monitoring}
                  </p>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-primary-50 p-4 rounded-xl border border-purple-200">
                  <p className="text-2xl font-bold text-primary-600">100%</p>
                  <p className="text-sm text-gray-600">
                    {getContent('aiSection').stats.personalization}
                  </p>
                </div>
              </div>
              <a
                href="/s"
                className="inline-flex items-center bg-gradient-to-r from-secondary-500 to-primary-500 text-white px-6 py-3 rounded-xl font-semibold hover:from-secondary-600 hover:to-primary-600 transition-all duration-300 shadow-lg shadow-purple-500/30 hover:shadow-xl hover:shadow-purple-500/40 transform hover:-translate-y-0.5"
              >
                {getContent('aiSection').cta}
                <ChevronRight className="ml-2 w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 bg-gradient-to-br from-purple-100 via-primary-100 to-secondary-100 relative">
        <div className="absolute inset-0 bg-purple-mesh opacity-5"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-dark mb-16 font-heading">
            <span className="bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
              {getContent('howItWorks').title}
            </span>
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: UserPlus, title: getContent('howItWorks').steps[0].title },
              { icon: Settings2, title: getContent('howItWorks').steps[1].title },
              { icon: Rocket, title: getContent('howItWorks').steps[2].title }
            ].map((step, index) => {
              const IconComponent = step.icon;
              return (
                <div key={index} className="text-center group">
                  <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-secondary-500 to-primary-500 text-white rounded-full mb-6 text-3xl font-bold shadow-lg shadow-purple-500/30 group-hover:shadow-xl group-hover:shadow-purple-500/40 transition-all duration-300 transform group-hover:scale-110">
                    {index + 1}
                  </div>
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-primary-500 to-secondary-500 text-white rounded-2xl mb-4 shadow-lg shadow-purple-500/30 group-hover:shadow-xl group-hover:shadow-purple-500/40 transition-all duration-300">
                    <IconComponent className="w-10 h-10" />
                  </div>
                  <h3 className="text-xl font-semibold text-dark font-heading">
                    {step.title}
                  </h3>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Progress Tracking Section */}
      <section className="py-20 bg-white relative">
        <div className="absolute inset-0 bg-gradient-to-b from-purple-50/50 to-transparent"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-dark mb-6 font-heading">
              <span className="bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
                {getContent('progressSection').title}
              </span>
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              {getContent('progressSection').subtitle}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <img 
                src="/images/progress-tracking.png" 
                alt="Progress tracking and analytics"
                className="rounded-2xl shadow-2xl w-full"
              />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-dark mb-4">
                {getContent('progressSection').visualTitle}
              </h3>
              <p className="text-lg text-gray-600 mb-6">
                {getContent('progressSection').visualDescription}
              </p>
              <ul className="space-y-3">
                {getContent('progressSection').items.map((item, index) => (
                  <li key={index} className="flex items-start">
                    <div className="flex-shrink-0 w-6 h-6 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-full flex items-center justify-center mr-3 mt-1">
                      <ChevronRight className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-gray-600">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {getContent('progressSection').cards.map((card, index) => {
              const icons = [TrendingUp, Award, Zap];
              const Icon = icons[index];
              return (
                <div key={index} className="text-center p-8 bg-gradient-to-br from-purple-50 to-primary-50 rounded-2xl border border-purple-200 hover:shadow-xl hover:shadow-purple-500/20 transition-all duration-300 transform hover:-translate-y-2">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary-500 to-secondary-500 text-white rounded-2xl mb-4 shadow-lg shadow-purple-500/30">
                    <Icon className="w-8 h-8" />
                  </div>
                  <h3 className="text-xl font-semibold text-dark mb-2">
                    {card.title}
                  </h3>
                  <p className="text-gray-600">
                    {card.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 bg-gradient-to-br from-purple-100 via-primary-100 to-secondary-100 relative">
        <div className="absolute inset-0 bg-purple-mesh opacity-5"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-dark mb-16 font-heading">
            <span className="bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
              {getContent('faq').title}
            </span>
          </h2>
          
          <div className="space-y-8">
            {getContent('faq').questions.map((item, index) => (
              <div key={index} className="border-b border-purple-200 pb-8 last:border-b-0 bg-white/50 backdrop-blur-sm rounded-2xl p-6 hover:shadow-lg hover:shadow-purple-500/20 transition-all duration-300">
                <h3 className="text-xl font-semibold text-dark mb-4 font-heading">
                  {item.question}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {item.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section id="cta" className="py-20 bg-gradient-to-br from-primary-600 via-secondary-600 to-accent-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-purple-mesh opacity-20"></div>
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-32 h-32 bg-white/10 rounded-full mix-blend-overlay filter blur-xl animate-purple-float"></div>
          <div className="absolute bottom-10 right-10 w-24 h-24 bg-white/10 rounded-full mix-blend-overlay filter blur-xl animate-purple-float" style={{animationDelay: '1s'}}></div>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-8 font-heading">
            {getContent('cta').title}
          </h2>
          <a
            href="/s"
            className="inline-flex items-center bg-white text-primary-600 px-10 py-5 rounded-xl text-xl font-semibold hover:bg-gray-100 transition-all duration-300 shadow-2xl shadow-black/20 hover:shadow-3xl hover:shadow-black/30 transform hover:-translate-y-1"
          >
            {getContent('cta').button}
            <ChevronRight className="ml-3 w-6 h-6" />
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-dark text-white py-12 relative">
        <div className="absolute inset-0 bg-gradient-to-t from-purple-900 to-dark"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-white mb-4">{getContent('footer').serviceTitle}</h3>
            <p className="text-lg text-purple-200 mb-4">
              {getContent('footer').serviceDescription}
            </p>
            <p className="text-md text-purple-300 mb-3">
              {getContent('footer').serviceDetails}
            </p>
            <p className="text-sm text-purple-300 mb-3">
              {getContent('footer').unsubscribe}
            </p>
            <p className="text-xs text-purple-400 mb-3">
              {getContent('footer').operatorCharges}
            </p>
            <p className="text-xs text-purple-300">
              {getContent('footer').termsConfirmation}
            </p>
            <p className="text-xs text-purple-300 mt-4">
              {getContent('footer').rightsNotice}
            </p>
          </div>
          
          <div className="flex justify-center space-x-6 mb-8">
            <a href="/privacy.html" className="hover:text-primary-400 transition-colors duration-200">
              {getContent('footer').privacy}
            </a>
            <span className="text-purple-400">|</span>
            <a href="/terms.html" className="hover:text-primary-400 transition-colors duration-200">
              {getContent('footer').terms}
            </a>
          </div>
          
          <p className="text-purple-300 mb-4 text-center">
            {getContent('footer').copyright.replace('{year}', new Date().getFullYear())}
          </p>
          
          <div className="border-t border-purple-800 pt-4">
            <p className="text-xs text-purple-400 text-center">
              {getContent('footer').domainOwner}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
